<script setup>
import { router } from '@inertiajs/vue3';
import AppLayout from '@/Layouts/AppLayout.vue';
import { ref, watch } from "vue";
import debounce from "lodash/debounce";
import FlatPickr from 'vue-flatpickr-component';
import Loading from 'vue-loading-overlay';
import dayjs from 'dayjs';
import advancedFormat from 'dayjs/plugin/advancedFormat';

const props = defineProps({
    year: String,
    arrival: Object,
    departure: Object,
    flash: Object,
    user: Object,
});

let arrdeptSearch = ref('');
let isLoading = ref(false);
dayjs.extend(advancedFormat);
let date = ref();

const config = ref({
    altFormat: 'J M, Y',
    altInput: true,
    dateFormat: 'Y-m-d',
});

watch([arrdeptSearch], debounce(function (value) {
    router.get('/reports/arrvdepartSearch', { date: value }, {
        preserveState: true,
        replace: true,
        onStart: () => isLoading.value = true,
        onFinish: () => isLoading.value = false,
    });
    date.value = value;
}, 300));

function getAdultChildInfant(clients) {
    let adult = 0;
    let child = 0;
    let infant = 0;
    clients.forEach((client) => {
        if (client.dob == null) {
            adult++;
        } else if (dayjs().diff(client.dob, 'year') < 2) {
            infant++;
        } else {
            child++;
        }
    });
    return 'Adult: ' + adult + ' / Child: ' + child + ' / Infant:' + infant;
}

</script>
<style scoped>
@import "../../../../public/ui/vendor/libs/toastr/toastr.css";
@import 'flatpickr/dist/themes/material_orange.css';
@import "vue-loading-overlay/dist/vue-loading.css";
</style>
<template>
    <AppLayout title="Arrival/Departure List" :year=year :user=user>
        <div class="vld-parent">
            <Loading v-model:active="isLoading" :color="'#696cff'" />
        </div>
        <div class="container-xxl flex-grow-1 container-p-y">
            <div class="card">
                <div class="card-header border-bottom">
                    <h5 class="card-title">Arrival/Departure List</h5>
                    <div class="row mx-2">
                        <div class="col-sm-12 col-md-4 col-lg-6"></div>
                        <div class="col-sm-12 col-md-8 col-lg-6">
                            <div
                                class="dt-action-buttons text-xl-end text-lg-start text-md-end text-start d-flex align-items-center justify-content-md-end justify-content-center align-items-center flex-sm-nowrap flex-wrap me-1">

                                <div class="w-px-200 pb-3 pb-sm-0 m-2">
                                    <div id="DataTables_Table_0_filter" class="dataTables_filter"><label>
                                            <FlatPickr :config="config" class="form-control" v-model="arrdeptSearch"
                                                placeholder="Select Date.." name="booking_date_from" />
                                        </label>
                                    </div>
                                </div>

                                <div class="btn-group">
                                    <button type="button" class="btn btn-label-primary dropdown-toggle"
                                        data-bs-toggle="dropdown" aria-expanded="false"><i
                                            class="bx bx-export me-sm-2"></i>
                                        Export
                                    </button>
                                    <ul class="dropdown-menu" style="">
                                        <li>
                                            <a class="dropdown-item" :href="'/reports/pdfArrvDepartRecordset/' + date"
                                                target="_blank"><i class="bx bxs-file-pdf me-2"></i>Pdf
                                            </a>
                                        </li>
                                    </ul>
                                </div>

                            </div>
                        </div>
                    </div>
                </div>
                <div class="card-datatable table-responsive" v-if="arrdeptSearch">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="text-center">
                                <h5>Arrivals</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3">
                                <div class="dt-buttons">
                                </div>
                            </div>
                        </div>

                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Booking Ref</th>
                                    <th>Property Name</th>
                                    <th>Guest Name</th>
                                    <th>Arrival Info</th>
                                    <th>Departure Info</th>
                                    <th>Nationality</th>
                                    <th>Agency</th>
                                    <th>Resort Ref</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="arrv in props.arrival">
                                    <td>{{ arrv.booking_no }}</td>
                                    <td>{{ arrv.hotel?.name }}</td>
                                    <td>{{ arrv.client[0]?.title }}. {{ arrv.client[0]?.first_name }} {{
                                        arrv.client[0]?.last_name
                                    }}
                                        <span><br>{{ getAdultChildInfant(arrv?.client) }}</span>
                                    </td>
                                    <td>{{ arrv.arrival_flight }}</td>
                                    <td>{{ arrv.departure_flight }}</td>
                                    <td>{{ arrv.countries[0].name }}</td>
                                    <td>{{ arrv.agent.agency_name }}</td>
                                    <td style="max-width: 300px;">{{ arrv.resort_confirmation_number }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>

            <div class="card mt-3" v-if="arrdeptSearch">
                <div class="card-datatable table-responsive">
                    <div id="DataTables_Table_0_wrapper" class="dataTables_wrapper dt-bootstrap5 no-footer">
                        <div class="card-header flex-column flex-md-row">
                            <div class="text-center">
                                <h5>Departures</h5>
                            </div>
                            <div class="dt-action-buttons text-end pt-3">
                                <div class="dt-buttons">
                                </div>
                            </div>
                        </div>

                        <table class="datatables-basic table border-top dataTable no-footer dtr-column"
                            id="DataTables_Table_0" role="grid" aria-describedby="DataTables_Table_0_info"
                            style="width: 1390px;">
                            <thead>
                                <tr role="row">
                                    <th>Booking Ref</th>
                                    <th>Property Name</th>
                                    <th>Guest Name</th>
                                    <th>Arrival Info</th>
                                    <th>Departure Info</th>
                                    <th>Nationality</th>
                                    <th>Agency</th>
                                    <th>Hotel Ref</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr class="odd" v-for="dept in props.departure">
                                    <td>{{ dept.booking_no }}</td>
                                    <td>{{ dept.hotel?.name }}</td>
                                    <td>{{ dept.client[0]?.title }}. {{ dept.client[0]?.first_name }} {{
                                        dept.client[0]?.last_name
                                    }}
                                        <span><br>{{ getAdultChildInfant(dept?.client) }}</span>
                                    </td>
                                    <td>{{ dept.arrival_flight }}</td>
                                    <td>{{ dept.departure_flight }}</td>
                                    <td>{{ dept.countries[0].name }}</td>
                                    <td>{{ dept.agent.agency_name }}</td>
                                    <td>{{ dept.resort_confirmation_number }}</td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        </div>
    </AppLayout>
</template>